import { TILE } from '../core/constants';
import type { PowerupType } from '../core/powerups';
import type { World } from '../core/world';
import type { Camera } from './camera';
import { sprite } from './sprites';

const POWERUP_STYLE: Record<PowerupType, { color: string; label: string }> = {
  doublejump: { color: '#5ad7e8', label: '2' },
  armor: { color: '#cfd6e6', label: 'A' },
  jump: { color: '#9bbc0f', label: 'J' },
  dash: { color: '#e0f8a0', label: 'D' },
  run: { color: '#f2a94c', label: 'R' },
};

/** First loaded sprite among the given keys (themed key first, then base), or null. */
function firstSprite(keys: string[]): HTMLImageElement | null {
  for (const k of keys) {
    const img = sprite(k);
    if (img) return img;
  }
  return null;
}

/** Draw a sprite if loaded for any of `key(s)`, otherwise run the shape fallback. */
function drawSprite(
  ctx: CanvasRenderingContext2D,
  key: string | string[],
  x: number,
  y: number,
  w: number,
  h: number,
  fallback: () => void,
): void {
  const img = Array.isArray(key) ? firstSprite(key) : sprite(key);
  if (img) ctx.drawImage(img, x, y, w, h);
  else fallback();
}

const COLORS = {
  bg: '#0f0f1b',
  solid: '#3b4a7a',
  solidTop: '#566bb0',
  spike: '#d9534f',
  goal: '#f2c94c',
  coin: '#f2c94c',
  diamond: '#5ad7e8',
  walker: '#c060c0',
  player: '#9bbc0f',
  playerDash: '#e0f8a0',
  hud: '#c8d0f0',
};

/** Draw the full world state for one frame. Reads only; never mutates the sim. */
export function render(
  ctx: CanvasRenderingContext2D,
  world: World,
  cam: Camera,
  viewW: number,
  viewH: number,
  levelName: string,
  theme = 'forest',
): void {
  // Background: themed full-viewport image if provided, else base, else flat fill.
  const bg = firstSprite([`bg_${theme}`, 'background']);
  if (bg) {
    ctx.drawImage(bg, 0, 0, viewW, viewH);
  } else {
    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, viewW, viewH);
  }

  const level = world.level;
  const ox = Math.round(cam.x);
  const oy = Math.round(cam.y);

  // Only draw tiles within the viewport.
  const tx0 = Math.max(0, Math.floor(cam.x / TILE));
  const tx1 = Math.min(level.width - 1, Math.floor((cam.x + viewW) / TILE));
  const ty0 = Math.max(0, Math.floor(cam.y / TILE));
  const ty1 = Math.min(level.height - 1, Math.floor((cam.y + viewH) / TILE));

  for (let ty = ty0; ty <= ty1; ty++) {
    for (let tx = tx0; tx <= tx1; tx++) {
      const t = level.tiles[ty][tx];
      const sx = tx * TILE - ox;
      const sy = ty * TILE - oy;
      if (t === 'solid') {
        drawSprite(ctx, [`tile_solid_${theme}`, 'tile_solid'], sx, sy, TILE, TILE, () => {
          ctx.fillStyle = COLORS.solid;
          ctx.fillRect(sx, sy, TILE, TILE);
          ctx.fillStyle = COLORS.solidTop;
          ctx.fillRect(sx, sy, TILE, 3); // lit top edge
        });
      } else if (t === 'spike') {
        drawSprite(ctx, [`tile_spike_${theme}`, 'tile_spike'], sx, sy, TILE, TILE, () =>
          drawSpike(ctx, sx, sy),
        );
      }
    }
  }

  // Goal flag.
  const gb = world.goalBox();
  drawSprite(ctx, 'goal', gb.x - ox, gb.y - oy, TILE, TILE, () => {
    ctx.fillStyle = COLORS.goal;
    ctx.fillRect(gb.x - ox, gb.y - oy, TILE, TILE);
  });

  // Coins (ghosts = already banked: faded, not pickable).
  for (const c of world.coins) {
    ctx.globalAlpha = c.ghost ? 0.22 : 1;
    drawSprite(ctx, 'coin', c.x - ox, c.y - oy, TILE, TILE, () => {
      ctx.fillStyle = COLORS.coin;
      ctx.beginPath();
      ctx.arc(c.x - ox + TILE / 2, c.y - oy + TILE / 2, 4, 0, Math.PI * 2);
      ctx.fill();
    });
  }
  ctx.globalAlpha = 1;

  // Diamonds (rare; ghosts faded).
  for (const d of world.diamonds) {
    ctx.globalAlpha = d.ghost ? 0.22 : 1;
    drawSprite(ctx, 'diamond', d.x - ox, d.y - oy, TILE, TILE, () => {
      const cx = d.x - ox + TILE / 2;
      const cy = d.y - oy + TILE / 2;
      ctx.fillStyle = COLORS.diamond;
      ctx.beginPath();
      ctx.moveTo(cx, cy - 6);
      ctx.lineTo(cx + 5, cy);
      ctx.lineTo(cx, cy + 6);
      ctx.lineTo(cx - 5, cy);
      ctx.closePath();
      ctx.fill();
    });
  }
  ctx.globalAlpha = 1;

  // Power-ups.
  for (const pu of world.powerups) {
    const style = POWERUP_STYLE[pu.type];
    drawSprite(ctx, `powerup_${pu.type}`, pu.x - ox, pu.y - oy, TILE, TILE, () => {
      ctx.fillStyle = style.color;
      ctx.fillRect(pu.x - ox + 2, pu.y - oy + 2, TILE - 4, TILE - 4);
      ctx.fillStyle = '#0f0f1b';
      ctx.font = '9px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(style.label, pu.x - ox + TILE / 2, pu.y - oy + TILE / 2);
      ctx.textAlign = 'left';
    });
  }

  // Walkers (themed enemy skin: forest spider / desert snake).
  for (const w of world.walkers) {
    drawSprite(ctx, [`enemy_${theme}`, 'walker'], w.body.x - ox, w.body.y - oy, w.body.w, w.body.h, () => {
      ctx.fillStyle = COLORS.walker;
      ctx.fillRect(w.body.x - ox, w.body.y - oy, w.body.w, w.body.h);
    });
  }

  // Player.
  const p = world.player;
  drawSprite(ctx, `player_${p.visual}`, p.body.x - ox, p.body.y - oy, p.body.w, p.body.h, () => {
    ctx.fillStyle = p.visual === 'dash' ? COLORS.playerDash : COLORS.player;
    ctx.fillRect(p.body.x - ox, p.body.y - oy, p.body.w, p.body.h);
  });

  drawHud(ctx, world, levelName);
}

function drawSpike(ctx: CanvasRenderingContext2D, sx: number, sy: number): void {
  ctx.fillStyle = COLORS.spike;
  ctx.beginPath();
  ctx.moveTo(sx, sy + TILE);
  ctx.lineTo(sx + TILE / 2, sy);
  ctx.lineTo(sx + TILE, sy + TILE);
  ctx.closePath();
  ctx.fill();
}

function drawHud(ctx: CanvasRenderingContext2D, world: World, levelName: string): void {
  ctx.fillStyle = COLORS.hud;
  ctx.font = '10px monospace';
  ctx.textBaseline = 'top';
  ctx.fillText(levelName, 6, 6);
  const secs = (world.timeMs / 1000).toFixed(1);
  ctx.fillText(`time ${secs}s   deaths ${world.deaths}`, 6, 18);
  ctx.fillText(`◆ ${world.diamondsCollected}   ● ${world.coinsCollected}`, 6, 30);
  const mods = activeMods(world);
  if (mods) ctx.fillText(mods, 6, 42);
}

function activeMods(world: World): string {
  const m = world.mods;
  const parts: string[] = [];
  if (m.doubleJump) parts.push('2xJUMP');
  if (m.armor || world.player.iframes > 0) parts.push(world.player.iframes > 0 ? 'INVULN' : 'ARMOR');
  if (m.jumpMult > 1) parts.push(`J×${m.jumpMult.toFixed(2)}`);
  if (m.dashMult > 1) parts.push(`D×${m.dashMult.toFixed(2)}`);
  if (m.runMult > 1) parts.push(`R×${m.runMult.toFixed(2)}`);
  return parts.join('  ');
}

/** Centered overlay message (e.g. "You win!"). */
export function renderOverlay(
  ctx: CanvasRenderingContext2D,
  viewW: number,
  viewH: number,
  text: string,
): void {
  ctx.fillStyle = 'rgba(15,15,27,0.75)';
  ctx.fillRect(0, 0, viewW, viewH);
  ctx.fillStyle = COLORS.goal;
  ctx.font = '20px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, viewW / 2, viewH / 2);
  ctx.textAlign = 'left';
}
